import { UPLOAD_PURPOSE_USER_AVATAR } from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";
import {
  putToSignedUrl,
  stageMemoryPreflightUpload,
  type UploadConfirmResult,
  type UploadPresignResult,
} from "@/features/preflight/api/uploads.api";

export interface UserAvatarUploadConfirmResult extends UploadConfirmResult {
  publicUrl?: string;
}

export async function presignUserAvatarUpload(input: {
  tenantId: string;
  contentType: string;
  sizeBytes: number;
  originalName: string;
}): Promise<UploadPresignResult> {
  const { data } = await authFetch<UploadPresignResult>("/uploads/presign", {
    method: "POST",
    body: JSON.stringify({
      tenantId: input.tenantId,
      purpose: UPLOAD_PURPOSE_USER_AVATAR,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
      originalName: input.originalName,
    }),
  });
  return data;
}

export async function confirmUserAvatarUpload(input: {
  tenantId: string;
  uploadId: string;
}): Promise<UserAvatarUploadConfirmResult> {
  const { data } = await authFetch<UserAvatarUploadConfirmResult>("/uploads/confirm", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function runUserAvatarUploadFlow(input: {
  tenantId: string;
  file: File;
}): Promise<UserAvatarUploadConfirmResult> {
  const presign = await presignUserAvatarUpload({
    tenantId: input.tenantId,
    contentType: input.file.type,
    sizeBytes: input.file.size,
    originalName: input.file.name,
  });

  if (presign.requiresMemoryStage || presign.requiresServerStage) {
    await stageMemoryPreflightUpload({
      tenantId: input.tenantId,
      uploadId: presign.uploadId,
      contentType: input.file.type,
      file: input.file,
    });
  } else {
    await putToSignedUrl(presign.uploadUrl, input.file, presign.requiredHeaders);
  }

  return confirmUserAvatarUpload({
    tenantId: input.tenantId,
    uploadId: presign.uploadId,
  });
}
